import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Phone, Users, Calendar, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'contacted', label: 'Contacted', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'converted', label: 'Converted', cls: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'closed', label: 'Closed', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
];

const statusStyle = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.cls || STATUS_OPTIONS[0].cls;

const TripInquiriesTab = ({ token }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/trip-inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(res.data || []);
    } catch (e) {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInquiries(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/admin/trip-inquiries/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try {
      await axios.delete(`${API_URL}/admin/trip-inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries((arr) => arr.filter((it) => it.id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Group by trip_id
  const grouped = inquiries
    .filter((q) => filter === 'all' || q.status === filter)
    .reduce((acc, q) => {
      const key = q.trip_destination || q.trip_id || 'Unknown trip';
      (acc[key] = acc[key] || []).push(q);
      return acc;
    }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#0A2342]" />
      </div>
    );
  }

  return (
    <div data-testid="trip-inquiries-tab">
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        <h2 className="text-lg font-semibold">Trip Inquiries ({inquiries.length})</h2>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Filter:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border rounded px-2 py-1"
            data-testid="trip-inquiries-filter"
          >
            <option value="all">All ({inquiries.length})</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({inquiries.filter((q) => q.status === o.value).length})
              </option>
            ))}
          </select>
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center text-gray-500">
          No enquiries match this filter yet.
        </div>
      ) : (
        Object.entries(grouped).map(([dest, list]) => (
          <div key={dest} className="bg-white border rounded-lg mb-4 overflow-hidden">
            <div className="bg-[#0A2342] text-white px-4 py-2 flex items-center justify-between">
              <h3 className="font-semibold">{dest}</h3>
              <Badge className="bg-[#FF6B35] text-white">{list.length} {list.length === 1 ? 'enquiry' : 'enquiries'}</Badge>
            </div>
            <div className="divide-y">
              {list.map((q) => (
                <div key={q.id} className="p-4 hover:bg-gray-50" data-testid={`inquiry-row-${q.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-[#0A2342]">{q.name}</p>
                      <p className="text-xs text-gray-500">
                        Submitted {new Date(q.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={q.status || 'new'}
                        onChange={(e) => setStatus(q.id, e.target.value)}
                        className={`text-xs border rounded px-2 py-1 ${statusStyle(q.status)}`}
                        data-testid={`inquiry-status-${q.id}`}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(q.id)} data-testid={`inquiry-delete-${q.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`mailto:${q.email}`} className="text-blue-600 hover:underline">{q.email}</a>
                    </div>
                    {q.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        <a href={`tel:${q.phone}`} className="text-blue-600 hover:underline">{q.phone}</a>
                      </div>
                    )}
                    {q.group_size && (
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-gray-400" />
                        Group size: <strong>{q.group_size}</strong>
                      </div>
                    )}
                    {q.preferred_dates && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        Preferred: <strong>{q.preferred_dates}</strong>
                      </div>
                    )}
                  </div>
                  {q.notes && (
                    <p className="mt-2 text-sm text-gray-600 bg-gray-50 border-l-2 border-gray-300 pl-3 py-1 whitespace-pre-wrap">
                      {q.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default TripInquiriesTab;
